import { useEffect, useRef, useCallback } from 'react';

/**
 * useIdleTimeout — auto-logs the user out after `timeoutMinutes` of
 * inactivity.  Activity is detected via mousemove, mousedown, keydown,
 * touchstart, scroll and visibility/focus changes.
 *
 * Setting `timeoutMinutes <= 0` disables the timer entirely.
 *
 * @param {number} timeoutMinutes  - minutes of idle before logout fires
 * @param {Function} onTimeout     - callback invoked on idle expiry
 * @param {boolean} enabled        - master switch (e.g. only when authenticated)
 */
export function useIdleTimeout(timeoutMinutes, onTimeout, enabled) {
  const onTimeoutRef = useRef(onTimeout);
  const timerRef = useRef(null);
  const lastActivityRef = useRef(Date.now());

  // Always call the latest onTimeout without retriggering the effect
  useEffect(() => { onTimeoutRef.current = onTimeout; }, [onTimeout]);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    // Disabled when not authenticated or timeout is 0
    if (!enabled || !timeoutMinutes || timeoutMinutes <= 0) {
      clearTimer();
      return undefined;
    }

    const timeoutMs = timeoutMinutes * 60 * 1000;

    const trigger = () => {
      // Fire only if we genuinely went idle for the full window
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= timeoutMs) {
        try { onTimeoutRef.current && onTimeoutRef.current(); } catch (_) { /* noop */ }
      } else {
        // Re-arm with the remaining window if there was late activity
        clearTimer();
        timerRef.current = setTimeout(trigger, timeoutMs - elapsed);
      }
    };

    const reset = () => {
      lastActivityRef.current = Date.now();
      clearTimer();
      timerRef.current = setTimeout(trigger, timeoutMs);
    };

    // Throttle reset to avoid CPU thrash on mousemove (every 5s)
    let lastReset = 0;
    const throttledReset = () => {
      const now = Date.now();
      if (now - lastReset > 5000) {
        lastReset = now;
        reset();
      } else {
        // Just record the activity timestamp without resetting timer immediately
        lastActivityRef.current = now;
      }
    };

    const events = ['mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach((evt) => window.addEventListener(evt, throttledReset, { passive: true }));
    // mousemove is throttled separately because it fires hundreds of times per second
    window.addEventListener('mousemove', throttledReset, { passive: true });
    // Coming back to the tab counts as activity
    window.addEventListener('focus', reset);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') reset();
    });

    // Start the initial timer
    reset();

    return () => {
      events.forEach((evt) => window.removeEventListener(evt, throttledReset));
      window.removeEventListener('mousemove', throttledReset);
      window.removeEventListener('focus', reset);
      // visibilitychange listener intentionally not removed (anonymous), it's
      // benign when no timer is set.
      clearTimer();
    };
  }, [enabled, timeoutMinutes, clearTimer]);
}
